import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NewProjectDialog from './sandbox/NewProjectDialog';

export const NEW_PATH = '/new';
const PENDING_KEY = 'flow.newProjectLink';

/** What a `/new` link asks for: `{ template, remix, name }` (each null when absent), or null when it asks for nothing. */
export function readNewProjectLink(search) {
  const params = new URLSearchParams(search || '');
  const pick = (key) => (params.get(key) || '').trim() || null;
  const link = { template: pick('template'), remix: pick('remix'), name: pick('name') };
  return link.template || link.remix || link.name ? link : null;
}

export function rememberLink(search) {
  try { sessionStorage.setItem(PENDING_KEY, search); } catch { /* private mode */ }
}
export function pendingLink() {
  try { return sessionStorage.getItem(PENDING_KEY); } catch { return null; }
}
export function forgetLink() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* private mode */ }
}

// The link is kept as soon as Flow loads: signing in redirects (and drops the query) before this component can read it.
// The router's basename is /flow (vite base), so the browser's path ends with /new.
if (typeof window !== 'undefined' && /\/new\/?$/.test(window.location.pathname) && window.location.search) {
  rememberLink(window.location.search);
}

/**
 * "New project" from a link (`/flow/new?template=<id>`, `/flow/new?remix=<projectId>`, optional `&name=`): once the
 * person is signed in, opens NewProjectDialog prefilled — the template selected in the gallery (an unknown one: the
 * gallery with a note), or in remix mode on that project. Closing it goes to Home, replacing the link in the history.
 * Mounted once, globally (App.jsx), like ConsoleHandoff; `/new` has its own route so `/:tenantId` does not take it.
 */
export default function NewProjectLink() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [link, setLink] = useState(null); // { template, remix, name, key }

  const atNew = location.pathname.replace(/\/$/, '') === NEW_PATH;

  useEffect(() => {
    if (!isAuthenticated || !user) { return; }
    const search = atNew && location.search ? location.search : pendingLink();
    if (!search && !atNew) { return; }
    forgetLink();
    setLink({ ...(readNewProjectLink(search) || { template: null, remix: null, name: null }), key: `${location.key}:${search}` });
  }, [isAuthenticated, user, atNew, location.search, location.key]);

  if (!link) { return null; }
  const close = () => {
    setLink(null);
    if (atNew) { navigate('/', { replace: true }); }
  };
  return (
    <NewProjectDialog
      initialName={link.name}
      initialTemplateId={link.remix ? null : link.template}
      key={link.key}
      onClose={close}
      open
      remixOf={link.remix}
    />
  );
}
